// ─────────────────────────────────────────────────────────────────────────────
// CompanySelector.tsx — THE BLOCKER, ON SCREEN.
//
// Buck has qualified 140 companies and there was no way to pick one. This is the
// pick, and the queue behind it. It sits on `Company Research & Diagnosis`, and
// the selection it writes is read by `Calls, Objections & Pivots` and
// `Consultation & Pitch` — one store, three readers (`lib/deltaSelection`).
//
// IT DECIDES NOTHING. Every rule it obeys is in a module with its own tests:
// `deltaSelection` holds the selection, `deltaQueue` holds his order,
// `deltaHandoff` says what a row may claim. This file renders what they return.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { MC } from '../missioncontrol/theme';
import {
  HandoffState, HANDOFF_UNREAD, readHandoff, selectable, dialable, researchable,
  displayName, HandoffCompany, UNKNOWN,
} from '../../api/deltaHandoff';
import {
  QueueEntry, enqueue, dequeue, isQueued, orderedQueue, queueDepth,
} from '../../lib/deltaQueue';
import {
  Selection, select, getSelection, setSelection, subscribeSelection,
} from '../../lib/deltaSelection';

const cell: React.CSSProperties = {
  padding: '8px 10px', borderBottom: `1px solid ${MC.line2}`, textAlign: 'left',
};

function Pill({ tone, children }: { tone: string; children: React.ReactNode }) {
  return (
    <span style={{
      color: tone, border: `1px solid ${tone}`, borderRadius: 999,
      padding: '1px 8px', fontSize: 11, letterSpacing: '.04em', whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

export interface CompanySelectorProps {
  /** Injected in tests; the live surface lets it fetch. */
  initial?: HandoffState;
  queue?: QueueEntry[];
  onQueueChange?: (q: QueueEntry[]) => void;
  /** THE QUEUE COLUMNS RENDER ONLY WHEN SOMETHING RECEIVES THEM. The ticks order
   *  a list held in this component and nothing else: no route takes a queue, and
   *  the batch is Work Selection Doctrine the owner has not yet written. A tick
   *  box that posts nowhere is worse than an absent one, so the live mount passes
   *  `false` until the doctrine and the route exist. Default true: tests unchanged. */
  queueable?: boolean;
}

export default function CompanySelector(props: CompanySelectorProps) {
  const [handoff, setHandoff] = React.useState<HandoffState>(props.initial || HANDOFF_UNREAD);
  const [selection, setSel] = React.useState<Selection>(getSelection());
  const [queue, setQueue] = React.useState<QueueEntry[]>(props.queue || []);

  React.useEffect(() => subscribeSelection(setSel), []);
  React.useEffect(() => {
    if (props.initial) return;               // a supplied payload is not refetched
    let alive = true;
    readHandoff().then((h) => { if (alive) setHandoff(h); });
    return () => { alive = false; };
  }, [props.initial]);

  const writeQueue = (next: QueueEntry[]) => {
    setQueue(next);
    if (props.onQueueChange) props.onQueueChange(next);
  };

  // UNREAD AND ABSENT ARE NOT EMPTY. Neither renders a table with no rows —
  // "Buck handed over nothing" is a different sentence from "we could not read
  // it", and the owner acts differently on each.
  if (handoff.phase !== 'read') {
    return (
      <div style={{ background: MC.panel, borderRadius: MC.radius, padding: 18, color: MC.muted }}>
        <div style={{ color: MC.text, fontWeight: 600, marginBottom: 6 }}>COMPANIES</div>
        <div style={{ color: handoff.phase === 'absent' ? MC.amber : MC.dim }}>
          {handoff.note || 'UNREAD'}
        </div>
      </div>
    );
  }

  const rows = handoff.rows;
  const pickable = selectable(rows);
  const depth = queueDepth(queue);
  const order = orderedQueue(queue);
  const positionOf = (id: string) => {
    const e = order.find((q) => q.companyId === id);
    return e ? e.position : null;
  };

  const queueable = props.queueable !== false;
  const row = (r: HandoffCompany, canPick: boolean) => {
    const chosen = selection.phase === 'selected' && selection.companyId === r.company_id;
    const pos = positionOf(r.company_id);
    return (
      <tr key={r.company_id}
          style={{ background: chosen ? MC.tealSoft : 'transparent' }}>
        {queueable && <td style={cell}>
          <input
            type="checkbox"
            aria-label={'queue ' + displayName(r)}
            disabled={!canPick}
            checked={isQueued(queue, r.company_id)}
            onChange={(e) => writeQueue(
              e.target.checked ? enqueue(queue, r.company_id) : dequeue(queue, r.company_id),
            )}
          />
        </td>}
        {/* HIS ORDER, AS THE NUMBER HE SET — never the row's place in the list. */}
        {queueable && <td style={{ ...cell, color: pos ? MC.teal : MC.dim, width: 40 }}>{pos || '—'}</td>}
        <td style={cell}>
          <button
            type="button"
            disabled={!canPick}
            onClick={() => setSelection(select(r.company_id))}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: canPick ? 'pointer' : 'default',
              color: canPick ? (chosen ? MC.teal : MC.text) : MC.dim,
              font: 'inherit', textAlign: 'left',
            }}
          >{displayName(r)}</button>
        </td>
        <td style={{ ...cell, color: MC.muted }}>{r.sector}</td>
        <td style={{ ...cell, color: MC.muted }}>{r.region}</td>
        {/* THE FIT FLAG (owner ruling 2026-10-06) — shown, never a gate. */}
        <td style={{ ...cell, color: MC.muted }}>
          {r.fit_flag && r.fit_flag !== UNKNOWN ? r.fit_flag : (r.fit == null ? UNKNOWN : r.fit)}
        </td>
        <td style={cell}>
          {dialable(r)
            ? <span style={{ color: MC.text }}>{r.phone}</span>
            : <Pill tone={MC.amber}>NO PHONE</Pill>}
        </td>
        <td style={cell}>
          {researchable(r) ? null : <Pill tone={MC.red}>NOT RESEARCHABLE</Pill>}
        </td>
      </tr>
    );
  };

  return (
    <div style={{ background: MC.panel, borderRadius: MC.radius, padding: 18, boxShadow: MC.cardShadow }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
        <div style={{ color: MC.text, fontWeight: 600 }}>COMPANIES</div>
        <div style={{ color: MC.muted, fontSize: 12 }}>
          {pickable.length} handed over{queueable ? ` · ${depth} queued` : ''}
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', color: MC.text, fontSize: 13 }}>
        <thead>
          <tr style={{ color: MC.dim, fontSize: 11, letterSpacing: '.06em' }}>
            {queueable && <th style={cell} />}{queueable && <th style={cell}>#</th>}<th style={cell}>COMPANY</th>
            <th style={cell}>SECTOR</th><th style={cell}>REGION</th><th style={cell}>FIT</th>
            <th style={cell}>PHONE</th><th style={cell} />
          </tr>
        </thead>
        <tbody>
          {pickable.map((r) => row(r, true))}
        </tbody>
      </table>
    </div>
  );
}
